import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { IMAGES } from "../lib/api";

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={go} data-testid="google-login-btn" className="w-full flex items-center justify-center gap-3 border border-[#27272a] hover:border-[#00f0ff] py-3 uppercase tracking-wider text-sm font-semibold transition-colors">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 bg-white rounded-sm p-0.5" />
      Continue with Google
    </button>
  );
};

function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 pt-16">
      <div className="relative hidden lg:block">
        <img src={IMAGES.truck2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050505]/70" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-display text-6xl leading-none"><span className="bttf-text">EXTREME</span> OFFSET</div>
          <p className="text-zinc-300 mt-3 max-w-sm">Track orders, save builds, and check out faster.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-5xl uppercase mb-8">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">{label}</label>
    <input {...props} className="w-full bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] py-3 px-3 outline-none transition-colors" />
  </div>
);

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate(dest); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Sign In">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <Field label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
        <button disabled={loading} data-testid="login-submit" className="w-full bttf-gradient text-black font-bold uppercase tracking-wider py-3.5 disabled:opacity-50">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-6 text-xs text-zinc-600 uppercase tracking-widest"><span className="flex-1 h-px bg-[#27272a]" />or<span className="flex-1 h-px bg-[#27272a]" /></div>
      <GoogleButton />
      <p className="text-sm text-zinc-400 mt-6">No account? <Link to="/register" className="text-[#00f0ff] hover:underline">Create one</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await register(email, password, name); navigate("/account"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create Account">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <Field label="Full Name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
        <Field label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
        <Field label="Password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
        {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
        <button disabled={loading} data-testid="register-submit" className="w-full bttf-gradient text-black font-bold uppercase tracking-wider py-3.5 disabled:opacity-50">
          {loading ? "Creating..." : "Create Account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-6 text-xs text-zinc-600 uppercase tracking-widest"><span className="flex-1 h-px bg-[#27272a]" />or<span className="flex-1 h-px bg-[#27272a]" /></div>
      <GoogleButton />
      <p className="text-sm text-zinc-400 mt-6">Have an account? <Link to="/login" className="text-[#00f0ff] hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}
